import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/ActivityLogsService';
import { useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

// FNP-328: when recordId / recordType are passed, list is filtered to the
// activity feed for that single entity (e.g. a customer-subscription detail
// page). When both are unset, behaves as before — full unfiltered list.
export const useActivityLogs = (recordId?: string, recordType?: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});

    const sort = { column: 'created', direction: 'desc' as const };

    const filter = recordId || recordType
        ? {
            ...(recordId ? { recordId } : {}),
            ...(recordType ? { recordType } : {}),
        }
        : undefined;

    // INFINITE QUERY
    const activityLogsQuery = useInfiniteQuery({
        queryKey: ['activity-logs', keyword, limit, sort.column, sort.direction, recordId ?? null, recordType ?? null],
        queryFn: ({ pageParam }) => fetchRecords({ filter, limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Usage record deleted');
            queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const fetchRecordsAction = () => {
        if (activityLogsQuery.hasNextPage) {
            activityLogsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = activityLogsQuery.data?.pages?.flatMap(page => page && 'items' in page ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: activityLogsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: activityLogsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};